'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@repo/ui';
import type { JSX } from 'react';
import { useMemo } from 'react';
import { EmptyState } from '../empty-state';
import { PipelineRow } from './pipeline-row';
import { useVideoCheck } from './use-video-check';

interface IPipelineDashboardProps {
  items: ICollectionPipelineStatusV3[];
}

const COLUMN_COUNT = 17;

export function PipelineDashboard({ items }: IPipelineDashboardProps): JSX.Element {
  const vaultPaths = useMemo(() => items.map((i) => i.vaultPath), [items]);
  const videoCheck = useVideoCheck(vaultPaths);

  const grouped = useMemo(() => {
    const groups = new Map<string, ICollectionPipelineStatusV3[]>();
    for (const item of items) {
      const path = item.collectionPath.split(' > ').slice(0, -1).join(' > ') || 'Uncategorized';
      const list = groups.get(path) ?? [];
      list.push(item);
      groups.set(path, list);
    }
    return groups;
  }, [items]);

  if (items.length === 0) {
    return <EmptyState title="No collections" description="Import SRT files to get started." />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Collection</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>SRT</TableHead>
          <TableHead>Video</TableHead>
          <TableHead>Match</TableHead>
          <TableHead>Matched Clips</TableHead>
          <TableHead>AI Analysis</TableHead>
          <TableHead>Manual</TableHead>
          <TableHead>Populate</TableHead>
          <TableHead>Translated</TableHead>
          <TableHead>Cropped</TableHead>
          <TableHead>Triaged</TableHead>
          <TableHead>Missing</TableHead>
          <TableHead>Rejected Clip</TableHead>
          <TableHead>Rejected Challenge</TableHead>
          <TableHead>Uploaded</TableHead>
          <TableHead>Published</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...grouped.entries()].map(([group, groupItems]) => (
          <GroupRows key={group} group={group} items={groupItems} videoCheck={videoCheck} />
        ))}
      </TableBody>
    </Table>
  );
}

interface IGroupRowsProps {
  group: string;
  items: ICollectionPipelineStatusV3[];
  videoCheck: Record<string, boolean>;
}

function GroupRows({ group, items, videoCheck }: IGroupRowsProps): JSX.Element {
  return (
    <>
      <TableRow>
        <td
          colSpan={COLUMN_COUNT}
          className="bg-muted/50 px-4 py-1.5 text-xs font-semibold text-muted-foreground"
        >
          {group}
        </td>
      </TableRow>
      {items.map((item) => (
        <PipelineRow
          key={item.collectionId}
          item={item}
          hasVideo={item.vaultPath ? videoCheck[item.vaultPath] : undefined}
        />
      ))}
    </>
  );
}
